import React, { useRef, useState } from 'react';

import {
  faChevronDown,
  faCopy,
  faEnvelope,
  faSquareCaretDown,
  faSquareCaretUp
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Title,
  Paper,
  Stack,
  Alert,
  Button,
  Card,
  Group,
  Text,
  Grid,
  Drawer,
  ActionIcon,
  List,
  Menu
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import moment from 'moment-timezone';
import { useParams, useNavigate, Link } from 'react-router-dom';

import { type Client } from '../components/client/types';
import EncounterList from '../components/encounter/EncounterList';
import ErrorState from '../components/state/ErrorState';
import LoadingState from '../components/state/LoadingState';
import { useAuth } from '../contexts/AuthContext';
import { MarkdownRenderer } from '../utils/markdown';
import { PostJSON, usePostJSON } from '../utils/request';
import { useTitle } from '../utils/title';

interface ClientResponse {
  success: boolean;
  client: Client;
}

interface MetaSummaryResponse {
  wellBeingSummary: string;
  attentionItems: string;
  suggestedResources: string[];
}

interface MetaSummaryAPIResponse {
  success: boolean;
  summary?: MetaSummaryResponse;
  error?: string;
}

const ClientEncounters: React.FC = () => {
  const { id: _id } = useParams<{ id: string }>();
  const id = _id ? parseInt(_id, 10) : undefined;
  const navigate = useNavigate();
  const { account } = useAuth();
  const [carePlanDrawerOpened, setCarePlanDrawerOpened] = useState(false);
  const [clientInfoCollapsed, setClientInfoCollapsed] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryResult, setSummaryResult] = useState<MetaSummaryResponse | null>(null);
  const [summaryDrawerOpen, setSummaryDrawerOpen] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    moment().subtract(2, 'weeks').toDate(),
    moment().toDate()
  ]);
  const summaryCache = useRef<Record<string, MetaSummaryResponse>>({});

  const setDateRangeSafe = (value: [Date | null, Date | null]) => {
    setDateRange([
      value[0] ? new Date(value[0]) : null,
      value[1] ? new Date(value[1]) : null,
    ]);
  };

  const matchesRange = (presetStart: Date, presetEnd: Date): boolean => {
    if (!dateRange[0] || !dateRange[1]) return false;
    return (
      moment(dateRange[0]).format('YYYY-MM-DD') === moment(presetStart).format('YYYY-MM-DD') &&
      moment(dateRange[1]).format('YYYY-MM-DD') === moment(presetEnd).format('YYYY-MM-DD')
    );
  };

  // Fetch client data to display client name
  const { data: clientResponse, error, loading } = usePostJSON<ClientResponse>(
    '/api/client/data_one',
    { id },
    [id]
  );

  const client = clientResponse?.client || null;
  useTitle(`${client?.firstName} ${client?.lastName} - Encounters`);

  const handleGenerateSummary = async () => {
    if (!id) return;

    const cacheKey = `${moment(dateRange[0]).format('YYYY-MM-DD')}:${moment(dateRange[1]).format('YYYY-MM-DD')}`;
    const cached = summaryCache.current[cacheKey];
    if (cached) {
      setSummaryResult(cached);
      setSummaryDrawerOpen(true);
      return;
    }

    setSummaryLoading(true);
    setSummaryError(null);
    setSummaryResult(null);

    try {
      const response = await PostJSON<MetaSummaryAPIResponse>(
        '/api/encounter/meta_summary',
        {
          clientId: id,
          startDate: dateRange[0]?.toISOString(),
          endDate: dateRange[1]?.toISOString()
        }
      );

      if (response.success && response.summary) {
        summaryCache.current[cacheKey] = response.summary;
        setSummaryResult(response.summary);
        setSummaryDrawerOpen(true);
      } else {
        setSummaryError(response.error || 'Failed to generate summary.');
      }
    } catch (err) {
      setSummaryError(
        err instanceof Error ? err.message : 'Failed to generate summary.'
      );
    } finally {
      setSummaryLoading(false);
    }
  };

  const getSummaryEmailText = (summary: MetaSummaryResponse, clientName: string) => {
    return [
      'Hi,',
      '',
      `Here is a summary update for ${clientName} from caregiver visits between ${dateRange[0] ? moment(dateRange[0]).format('MMM D, YYYY') : ''} and ${dateRange[1] ? moment(dateRange[1]).format('MMM D, YYYY') : ''}.`,
      '',
      '-- Overall Well-being --',
      summary.wellBeingSummary,
      '',
      '-- Items to Pay Attention To --',
      summary.attentionItems,
      '',
      '-- Suggested Resources & Products --',
      ...summary.suggestedResources.map((item, i) => `${i + 1}. ${item}`),
      '',
      'If you have any questions, please feel free to reach out.',
      '',
      'Regards,',
      account?.name || ''
    ].join('\n');
  };

  const getSummaryMailtoLink = (summary: MetaSummaryResponse, clientName: string) => {
    const subject = `Family Update: ${clientName}`;
    const body = getSummaryEmailText(summary, clientName);
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const copySummaryToClipboard = async (summary: MetaSummaryResponse, clientName: string) => {
    const text = getSummaryEmailText(summary, clientName);
    await navigator.clipboard.writeText(text);
    notifications.show({
      title: 'Copied',
      message: 'Email text copied to clipboard',
      color: 'green',
      autoClose: 2000,
    });
  };

  // Handle missing ID
  if (!id) {
    return (
      <Stack gap="md">
        <Title order={1}>Client Encounters</Title>
        <Paper withBorder p="md">
          <ErrorState
            message="Client ID is required"
            style={{ minHeight: '200px' }}
            title="Invalid Client ID"
          />
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/clients')}>
              Back to Clients
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  if (loading) {
    return (
      <LoadingState 
        message="Loading client data..." 
        style={{ height: '50vh' }} 
      />
    );
  }

  if (error) {
    return (
      <Stack gap="md">
        <Title order={1}>Client Encounters</Title>
        <Paper withBorder p="md">
          <ErrorState
            message={error.message}
            style={{ minHeight: '200px' }}
            title="Error Loading Client"
          />
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/clients')}>
              Back to Clients
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  if (!client) {
    return (
      <Stack gap="md">
        <Title order={1}>Client Encounters</Title>
        <Paper withBorder p="md">
          <Alert color="red" title="Client Not Found">
            The requested client could not be found.
          </Alert>
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/clients')}>
              Back to Clients
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group align="center" justify="space-between">
        <Button variant="outline" onClick={() => void navigate('/clients')}>
          Back to All Clients
        </Button>
        <Button 
          component={Link}
          to={`/client/edit/${client.id}`}
          variant="outline" 
        >
          Edit Client
        </Button>
      </Group>

      <div style={{ marginTop: '0.5rem' }} />

      <Paper withBorder p="md" shadow="sm">
        <Stack gap="md">
          {clientInfoCollapsed ? (
            <Group align="center" justify="space-between">
              <Text fw={600} size="xl">
                {client.firstName} {client.lastName}
              </Text>
              {
                client._agent && (
                  <Text c="dimmed" size="md">
                    Phono Phone: <Text c="blue" component="span" fw={600} size="md">{client._agent.phone}</Text>
                  </Text>
                )
              }
              <Group align="center" gap="xs">
                <ActionIcon
                  variant="subtle"
                  onClick={() => setClientInfoCollapsed(!clientInfoCollapsed)}
                >
                  <FontAwesomeIcon icon={faSquareCaretDown} size="sm" />
                </ActionIcon>
              </Group>
            </Group>
          ) : (
            <>
              <Group align="center" justify="space-between">
                <Title order={2} size="h3">Client Information</Title>
                <ActionIcon
                  variant="subtle"
                  onClick={() => setClientInfoCollapsed(!clientInfoCollapsed)}
                >
                  <FontAwesomeIcon icon={faSquareCaretUp} size="sm" />
                </ActionIcon>
              </Group>
            <Grid>
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text c="dimmed" fw={500} size="sm">Name</Text>
                <Text fw={600} size="lg">
                  {client.firstName} {client.lastName}
                </Text>
              </Stack>
            </Grid.Col>

            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text c="dimmed" fw={500} size="sm">Date of Birth</Text>
                <Text size="md">
                  {client.dob ? moment(client.dob).format('YYYY-MM-DD') : <Text c="dimmed" size="lg">NOT PROVIDED</Text>}
                </Text>
              </Stack>
            </Grid.Col>
            
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text c="dimmed" fw={500} size="sm">Client Phone</Text>
                <Text size="md">
                  {client.phone || <Text c="dimmed" size="lg">NOT PROVIDED</Text>}
                </Text>
              </Stack>
            </Grid.Col>
            
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text c="dimmed" fw={500} size="sm">Email</Text>
                <Text size="md">
                  {client.email || <Text c="dimmed" size="lg">NOT PROVIDED</Text>}
                </Text>
              </Stack>
            </Grid.Col>
            
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text c="dimmed" fw={500} size="sm">Care Plan</Text>
                <Group>
                  <Button
                    disabled={!client.carePlan}
                    variant="light"
                    onClick={() => setCarePlanDrawerOpened(true)}
                  >
                    {client.carePlan ? 'View Care Plan' : 'NO CARE PLAN AVAILABLE'}
                  </Button>
                </Group>
              </Stack>
            </Grid.Col>

            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text c="dimmed" fw={500} size="sm">Phono Number</Text>
                <Text c={client._agent ? 'default' : 'dimmed'} size="md">
                  {client._agent ? client._agent.phone : 'NOT ASSIGNED'}
                </Text>
              </Stack>
            </Grid.Col>
            
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text c="dimmed" fw={500} size="sm">Created</Text>
                <Text c="dimmed" size="md">
                  {new Date(client.createdAt).toLocaleDateString()}
                </Text>
              </Stack>
            </Grid.Col>
            
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Text c="dimmed" fw={500} size="sm">Last Updated</Text>
                <Text c="dimmed" size="md">
                  {new Date(client.updatedAt).toLocaleDateString()}
                </Text>
              </Stack>
            </Grid.Col>
            
            </Grid>
            </>
          )}
        </Stack>
      </Paper>

      <div style={{ marginTop: '0.5rem' }} />

      <Paper withBorder p="md" shadow="sm">
        <Stack gap="md">
          <Title order={3}>Family Update</Title>
          <Text c="dimmed" size="sm">
            Generate an AI-powered summary of recent encounters to share with the family.
          </Text>
          <Group align="flex-end" gap="md">
            <DatePickerInput
              allowSingleDateInRange
              firstDayOfWeek={0}
              label="Date range"
              maxDate={new Date()}
              placeholder="Select date range"
              type="range"
              value={dateRange}
              onChange={(value) => setDateRangeSafe(value as [Date | null, Date | null])}
            />
            <Button
              disabled={!dateRange[0] || !dateRange[1]}
              loading={summaryLoading}
              variant="filled"
              onClick={() => void handleGenerateSummary()}
            >
              Generate Summary
            </Button>
          </Group>
          <Group gap="xs">
            <Button
              size="xs"
              variant={matchesRange(
                moment().subtract(7, 'days').toDate(),
                moment().toDate()
              ) ? 'filled' : 'light'}
              onClick={() => setDateRange([
                moment().subtract(7, 'days').toDate(),
                moment().toDate(),
              ])}
            >
              Past Week
            </Button>
            <Button
              size="xs"
              variant={matchesRange(
                moment().subtract(14, 'days').toDate(),
                moment().toDate()
              ) ? 'filled' : 'light'}
              onClick={() => setDateRange([
                moment().subtract(14, 'days').toDate(),
                moment().toDate(),
              ])}
            >
              Past 2 Weeks
            </Button>
            <Button
              size="xs"
              variant={matchesRange(
                moment().subtract(30, 'days').toDate(),
                moment().toDate()
              ) ? 'filled' : 'light'}
              onClick={() => setDateRange([
                moment().subtract(30, 'days').toDate(),
                moment().toDate(),
              ])}
            >
              Past Month
            </Button>
          </Group>
          {summaryError && (
            <Alert color="red" title="Error">
              {summaryError}
            </Alert>
          )}
        </Stack>
      </Paper>

      <div style={{ marginTop: '0.5rem' }} />

      <EncounterList
        clientId={id}
        title={`Encounters for ${client.firstName} ${client.lastName}`}
      />

      <Drawer
        opened={carePlanDrawerOpened}
        position="right"
        size="lg"
        title="Care Plan Details"
        onClose={() => setCarePlanDrawerOpened(false)}
      >
        <Stack gap="md">
          <MarkdownRenderer content={client.carePlan || ''} />
        </Stack>
      </Drawer>

      <Drawer
        opened={summaryDrawerOpen}
        position="right"
        size="lg"
        title={`Family Update - ${client.firstName} ${client.lastName}`}
        onClose={() => setSummaryDrawerOpen(false)}
      >
        {summaryResult && (
          <Stack gap="md">
            <Group justify="flex-start">
              <Group gap={0}>
                <Button
                  color="blue"
                  component="a"
                  href={getSummaryMailtoLink(
                    summaryResult,
                    `${client.firstName} ${client.lastName}`
                  )}
                  leftSection={<FontAwesomeIcon icon={faEnvelope} size="sm" />}
                  style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                  target="_blank"
                  variant="filled"
                >
                  Email Family Update
                </Button>
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <Button
                      color="blue"
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: 8, paddingRight: 8 }}
                      variant="filled"
                    >
                      <FontAwesomeIcon icon={faChevronDown} size="xs" />
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<FontAwesomeIcon icon={faCopy} size="sm" />}
                      onClick={() => void copySummaryToClipboard(
                        summaryResult,
                        `${client.firstName} ${client.lastName}`
                      )}
                    >
                      Copy Email Text
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
            <Card withBorder p="md">
              <Stack gap="sm">
                <Title order={4}>Overall Well-being</Title>
                <MarkdownRenderer content={summaryResult.wellBeingSummary} />
              </Stack>
            </Card>
            <Card withBorder p="md">
              <Stack gap="sm">
                <Title order={4}>Items to Pay Attention To</Title>
                <MarkdownRenderer content={summaryResult.attentionItems} />
              </Stack>
            </Card>
            <Card withBorder p="md">
              <Stack gap="sm">
                <Title order={4}>Suggested Resources & Products</Title>
                <List spacing="xs" type="ordered">
                  {summaryResult.suggestedResources.map((item, i) => (
                    <List.Item key={i}>{item}</List.Item>
                  ))}
                </List>
              </Stack>
            </Card>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
};

export default ClientEncounters;
